import { Injectable } from "@angular/core";
import { Http, Response } from "@angular/http";
import {
    IDSVpnConnection,
    ITCIAppConfiguration,
    ITCIAppEnvControlProviderRequest,
    ITCIAppEnvControlProviderResponse,
    ITCIApplication,
    ITCIApplicationDeploymentProvider,
    ITCIApplicationDeployResponse,
    ITCIApplicationDeployRequest,
    TCIApplicationDeploymentMode,
    SERVICE_URLS,
    TCI_APP_PROPERTY_TYPE,
    TCI_ENV_CTRL_ACTION_TYPE,
    TCITenantAppType,
    ITCIApplicationAuth,
    BASE_CONSTANTS
} from "@tci/common";
import { NgAppListService } from "../../../ng-app-list/ng-app-list.service";
import { TciTenantModelService } from "../../../ng-tenant/tci-tenant.model";
import { BaseTCIAppEnvControlProvider } from "../../../ng-tenant/tci-tenant-services";
import { NgUserService } from "../../../ng-shared/ng-services/ng-user.service";

import { NgSecurityService } from "../../../ng-shared/ng-services/ng-security.service";
import {
    ITCIAppRefreshNotification,
    NgAppChangeNotificationService
} from "../../../ng-shared/ng-services/ng-app-change-notification.service";
import { Observable, Subject } from "rxjs";
import * as lodash from "lodash";

@Injectable()
export class BaseAppDetailEnvService extends BaseTCIAppEnvControlProvider {
    private static instance: BaseAppDetailEnvService = null;

    // Store the initial statuses to compare with the changes being made by the user
    private initialStatuses: { [key: string]: any } = {};

    // Index of changes made
    private changes: { [key: string]: boolean } = {};

    private appStatusSubject$ = new Subject<ITCIApplicationDeployResponse>();

    public static getInstance(http: Http, security: NgSecurityService, tenantModelSvc: TciTenantModelService, userService: NgUserService,
                              appsAPISrv: NgAppListService, appRefreshNotifSvc: NgAppChangeNotificationService) {
        if (!BaseAppDetailEnvService.instance) {
            BaseAppDetailEnvService.instance = new BaseAppDetailEnvService(http, security, tenantModelSvc, userService,
                appsAPISrv, appRefreshNotifSvc);
        }
        return BaseAppDetailEnvService.instance;
    }

    constructor(
        private http: Http,
        private security: NgSecurityService,
        private tenantModelSvc: TciTenantModelService,
        private userService: NgUserService,
        private appsAPISrv: NgAppListService,
        private appRefreshNotifSvc: NgAppChangeNotificationService
    ) {
        super();

        // Initialize index of changes for all properties to false
        Object.values(TCI_ENV_CTRL_ACTION_TYPE).forEach(prop => this.changes[prop] = false);
    }

    getAppRefreshNotifSubj(): Subject<ITCIAppRefreshNotification> {
        return this.appRefreshNotifSvc.getRefreshSubject();
    }

    getAppStatusSubject(): Subject<ITCIApplicationDeployResponse> {
        return this.appStatusSubject$;
    }

    getAppStatus(app: ITCIApplication): ITCIApplicationDeployResponse {
        return this.getTenantDeployProvider(app.type).getStatusSnapshot(app);
    }

    setAppStatus(appStatus: ITCIApplicationDeployResponse) {
        this.getAppStatusSubject().next(appStatus);
    }

    getTenantDeployProvider(tenantType: TCITenantAppType): ITCIApplicationDeploymentProvider {
        return this.tenantModelSvc.getTenant(tenantType).getAppDeploymentProvider();
    }

    setTabData(key: TCI_ENV_CTRL_ACTION_TYPE, data: any) {
        // Index change
        if (lodash.isUndefined(data)) { // Clean changes
            this.changes[key] = false;
        } else {
            this.changes[key] = !lodash.isEqual(data, this.initialStatuses[key]);
        }

        this.tabdata[key] = lodash.cloneDeep(data);
        this.getStatusSubject().next(key);
    }

    getAppLiveStatus(appId: string): Observable<ITCIApplicationDeployResponse> {
        return this.http.get(`${SERVICE_URLS.DEPLOYER_STATUS}/${appId}`)
            .map(res => res.json() as ITCIApplicationDeployResponse);
    }

    getAppLiveDetail(appId: string, appType?: TCITenantAppType): Observable<ITCIApplication> {
        return this.appsAPISrv.getAppById(appId, appType);
    }

    private requestData(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse> {
        // RETEST
        let url = `${SERVICE_URLS.APPS}/${request.appId}/configuration?appType=${request.tenantType}`;
        if (request?.isOnPremise) {
            url += `&isOnPremise=${request.isOnPremise}`;
        }
        return this.http.get(url)
            .map(res => res.json() as ITCIAppEnvControlProviderResponse)
            .catch(err => {
                console.error("Error at getting application configuration", request, err.json());
                const res: ITCIAppEnvControlProviderResponse = lodash.assign(request, {
                    appId: request.appId,
                    success: false,
                    msg: err.json().errorMsg,
                    data: null
                });
                return Observable.throw(res);
            });
    }

    private pushData(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse> {
        return this.http.put(`${SERVICE_URLS.APPS}/${request.appId}/configuration`, request)
            .map(res => res.json() as ITCIAppEnvControlProviderResponse)
            .catch(err => {
                console.error("Error at pushing application configuration", request, err.json());
                const res: ITCIAppEnvControlProviderResponse = lodash.assign(request, {
                    appId: request.appId,
                    success: false,
                    msg: err.json().errorMsg,
                    data: null
                });
                return Observable.throw(res);
            });
    }

    private commonPushEvaluation(request: ITCIAppEnvControlProviderRequest): boolean {
        return this.changes[request.actionType];
    }

    private commonPublish(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse> {
        // Do the update request
        return this.pushData(request)
            .map(res => {
                // Refresh stored values
                this.initialStatuses[request.actionType] = lodash.cloneDeep(this.tabdata[request.actionType]);

                // Reset the change state
                this.changes[request.actionType] = false;

                const data: ITCIAppConfiguration = { // This may come from the API PuT request
                    properties: null,
                    userSysProperties: null,
                    propertyOverrides: null,
                    propertyPrefix: null,
                    nativeSysPropertyOverrides: null
                };
                return {
                    appId: request.appId,
                    actionType: request.actionType,
                    success: true,
                    data
                } as ITCIAppEnvControlProviderResponse;
            });
    }

    private cleanPassword(resData: ITCIAppConfiguration) {
        lodash.forEach(resData.properties, property => {
            if (property.datatype === TCI_APP_PROPERTY_TYPE.password) {
                property.value = "";
                lodash.remove(resData.propertyOverrides, item => item.name === property.name);
            }
        });

        lodash.forEach(resData.nativeSysProperties, property => {
            if (property.datatype === TCI_APP_PROPERTY_TYPE.password) {
                property.value = "";
                lodash.remove(resData.nativeSysPropertyOverrides, item => item.name === property.name);
            }
        });
        return resData;
    }

    initApplicationProperties(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse> {
        return this.requestData(request)
            .filter(res => res.success)
            .map(res => {
                res.data = this.cleanPassword(res.data);
                // Set the initial data state
                this.initialStatuses[TCI_ENV_CTRL_ACTION_TYPE.APP_PROPERTIES] = {
                    "propertyOverrides": lodash.cloneDeep(res.data.propertyOverrides),
                    "postConfigData": res.data
                };
                return res;
            });
    }

    getAppProperties(request: ITCIAppEnvControlProviderRequest) {
        const url = `${SERVICE_URLS.APPS}/${request.appId}/configuration/appproperties`;
        return this.http.get(url).map((response: Response) => response);
    }

    canPushApplicationProperties(request: ITCIAppEnvControlProviderRequest): Observable<boolean> {
        return Observable.of(this.commonPushEvaluation(request));
    }

    pushApplicationProperties(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse> {
        return this.commonPublish(request);
    }

    initEngineProperties(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse> {
        return this.requestData(request)
            .filter(res => res.success)
            .map(res => {
                res.data = this.cleanPassword(res.data);
                this.initialStatuses[TCI_ENV_CTRL_ACTION_TYPE.ENGINE_PROPERTIES] = {
                    "nativeSysPropertyOverrides": lodash.cloneDeep(res.data.nativeSysPropertyOverrides),
                    "postConfigData": res.data
                };
                return res;
            });
    }
    generateReqInput(action_type: TCI_ENV_CTRL_ACTION_TYPE, app: ITCIApplication): ITCIAppEnvControlProviderRequest {
        return {
            actionType: action_type,
            appId: app.id,
            application: app,
            tenantType: app.type,
            isOnPremise: app.remoteManagement || false
        };
    }

    // IPAS-14136 getChanges method is used to detect the changes in env controls tab
    getChanges(app: ITCIApplication): Observable<{
        canPushApplicationProperties: boolean,
        canPushAppVPNConnection: boolean,
        canPushEngineProperties: boolean,
        canPushHybridConnectivity: boolean,
        canPushAppAutoScale: boolean
    }> {
        const obs = Observable.combineLatest(
            this.canPushApplicationProperties(this.generateReqInput(TCI_ENV_CTRL_ACTION_TYPE.APP_PROPERTIES, app)),
            this.canPushAppVPNConnection(this.generateReqInput(TCI_ENV_CTRL_ACTION_TYPE.APP_VPN, app)),
            this.canPushEngineProperties(this.generateReqInput(TCI_ENV_CTRL_ACTION_TYPE.ENGINE_PROPERTIES, app)),
            this.canPushHybridConnectivity(this.generateReqInput(TCI_ENV_CTRL_ACTION_TYPE.HYBRID_CONNECTIVITY, app)),
            this.canPushAppAutoScale(this.generateReqInput(TCI_ENV_CTRL_ACTION_TYPE.APP_AUTO_SCALE, app)),
            (
                canPushApplicationProperties,
                canPushAppVPNConnection,
                canPushEngineProperties,
                canPushHybridConnectivity,
                canPushAppAutoScale
            ) => (
                {
                    canPushApplicationProperties,
                    canPushAppVPNConnection,
                    canPushEngineProperties,
                    canPushHybridConnectivity,
                    canPushAppAutoScale
                }
            ));

        return obs;
    }

    canPushEngineProperties(request: ITCIAppEnvControlProviderRequest): Observable<boolean> {
        return Observable.of(this.commonPushEvaluation(request));
    }

    pushEngineProperties(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse> {
        return this.commonPublish(request);
    }

    // Hybrid Connectivity
    initHybridConnectivity(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse> {
        this.initialStatuses[TCI_ENV_CTRL_ACTION_TYPE.HYBRID_CONNECTIVITY] = request.application.tibTunnelAccessKey;
        return this
            .security
            .getTibTunnelAccessKeys(request.appId)
            .map(res => {
                return {
                    appId: request.appId,
                    actionType: request.actionType,
                    success: true,
                    accessKeyList: res
                } as ITCIAppEnvControlProviderResponse;
            });
    }

    canPushHybridConnectivity(request: ITCIAppEnvControlProviderRequest): Observable<boolean> {
        return Observable.of(this.commonPushEvaluation(request));
    }

    pushHybridConnectivity(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse> {
        const accessKey = this.getTabData(request.actionType);
        if (accessKey) {
            return this
            .security
            .getTibTunnelAccessKeys(request.appId)
            .catch(err => {
                return Observable.of(null);
            })
            .flatMap(data => {
                if (data && data.indexOf(accessKey) !== -1) {
                    return this
                       .security
                       .updateTibTunnelAccessKey(request.application.sandboxId, request.appId, accessKey)
                       .map(() => {
                           this.cleanChanges();
                           this.initialStatuses[TCI_ENV_CTRL_ACTION_TYPE.HYBRID_CONNECTIVITY] = accessKey;
                           return {
                               appId: request.appId,
                               actionType: request.actionType,
                               success: true,
                               defaultAccessKey: accessKey
                           } as ITCIAppEnvControlProviderResponse;
                       });
                } else {
                    const res: ITCIAppEnvControlProviderResponse =  lodash.assign(request, {
                        appId: request.appId,
                        actionType: request.actionType,
                        success: false,
                        defaultAccessKey: null,
                        msg: "The selected Access Key no longer exists, please refresh page and try again."
                    });
                    return Observable.throw(res);
                }
            });
        } else {
            return this
                .security
                .detachTibTunnelAccessKeys(request.application.sandboxId, request.appId)
                .map(() => {
                    this.cleanChanges();
                    this.initialStatuses[TCI_ENV_CTRL_ACTION_TYPE.HYBRID_CONNECTIVITY] = accessKey;
                    return {
                        appId: request.appId,
                        actionType: request.actionType,
                        success: true,
                        defaultAccessKey: accessKey
                    } as ITCIAppEnvControlProviderResponse;
                });
        }
    }

    initAppVPNConnection(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse> {
        this.initialStatuses[TCI_ENV_CTRL_ACTION_TYPE.APP_VPN] = request.application.vpnConnectionId;
        return Observable.create(obs => {
            this.userService.getUserInfo().subscribe(user => {
                this
                    .security
                    .getVpnConnections(user.userId)
                    .subscribe(res => {
                        obs.next({ appId: request.appId, actionType: request.actionType, success: true, vpnList: res });
                        obs.complete();
                    });
            });
        });
    }

    getVpnConnectionById(selectedVPN: string): Observable<IDSVpnConnection> {
        return Observable.create(obs => {
            this.userService.getUserInfo().subscribe(user => {
                this
                    .security
                    .getVpnConnectionById(user.userId, selectedVPN)
                    .subscribe(res => {
                        obs.next(res);
                        obs.complete();
                    }, err => {
                        obs.error(err);
                        obs.complete();
                    });
            });
        });
    }

    canPushAppVPNConnection(request: ITCIAppEnvControlProviderRequest): Observable<boolean> {
        return Observable.of(this.commonPushEvaluation(request));
    }

    pushAppVPNConnection(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse> {
        const vpnConnectionId = this.getTabData(request.actionType);
        if (vpnConnectionId) {
            return this.getVpnConnectionById(vpnConnectionId)
            .catch(err => {
                return Observable.of(null);
            })
            .flatMap(data => {
                if (data) {
                    return this
                        .security
                        .attachVpnConnection(request.application.sandboxId, request.appId, vpnConnectionId)
                        .map(() => {
                            this.cleanChanges();
                            this.initialStatuses[TCI_ENV_CTRL_ACTION_TYPE.APP_VPN] = vpnConnectionId;
                            return {
                                appId: request.appId,
                                actionType: request.actionType,
                                success: true,
                                defaultVPNConnectionId: vpnConnectionId
                            } as ITCIAppEnvControlProviderResponse;
                        });
                } else {
                    const res: ITCIAppEnvControlProviderResponse =  lodash.assign(request, {
                        appId: request.appId,
                        actionType: request.actionType,
                        success: false,
                        defaultVPNConnectionId: null,
                        msg: "The selected VPN no longer exists, please refresh page and try again."
                    });
                    return Observable.throw(res);
                }
            });
        } else {
            return this
                .security
                .detachVPNConnection(request.application.sandboxId, request.appId)
                .map(() => {
                    this.cleanChanges();
                    this.initialStatuses[TCI_ENV_CTRL_ACTION_TYPE.APP_VPN] = vpnConnectionId;
                    return {
                        appId: request.appId,
                        actionType: request.actionType,
                        success: true,
                        defaultVPNConnectionId: vpnConnectionId
                    } as ITCIAppEnvControlProviderResponse;
                });
        }
    }

    initAppAutoScale(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse> {
        this.initialStatuses[TCI_ENV_CTRL_ACTION_TYPE.APP_AUTO_SCALE] = lodash.cloneDeep(request.application.autoScaleConfig);
        return Observable.of({
            appId: request.appId,
            actionType: request.actionType,
            success: true,
            autoScaleConfig: request.application.autoScaleConfig
        });
    }

    /**
     * check if there is an update
     */
    canPushAppAutoScale(request: ITCIAppEnvControlProviderRequest): Observable<boolean> {
        return Observable.of(this.commonPushEvaluation(request));
    }

    /**
     * handle app autoscale update
     * @param request
     */
    pushAppAutoScale(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse> {
        return Observable.create(observer => {
            this.http.put(`${SERVICE_URLS.APPS}/${request.appId}/configuration${BASE_CONSTANTS.AUTO_SCALE_PROP}`, request)
                .subscribe(data => {
                    // Note: base on the validate api result to show error message or call autoscale app api
                    return this.http.put(`${SERVICE_URLS.APPS}/${request.appId}/configuration${BASE_CONSTANTS.AUTO_SCALE_CONFIG}`, request)
                        .subscribe(res => {
                            this.cleanChanges();
                            const resData: ITCIAppEnvControlProviderResponse = {
                                appId: request.appId,
                                actionType: request.actionType,
                                success: true,
                                autoScaleConfig: request.autoScaleConfig
                            };
                            this.initialStatuses[TCI_ENV_CTRL_ACTION_TYPE.APP_AUTO_SCALE] = lodash.cloneDeep(resData.autoScaleConfig);
                            this.getTenantDeployProvider(request.tenantType).updateAutoScale({
                                mode: TCIApplicationDeploymentMode.MODE_APP_DETAILS_ENV_CTRL,
                                appId: request.appId,
                                autoScaleConfig: request.autoScaleConfig} as ITCIApplicationDeployRequest);
                            observer.next(resData);
                            observer.complete();
                        }, err => {
                            const res: ITCIAppEnvControlProviderResponse = lodash.assign(request, {
                                appId: request.appId,
                                success: false,
                                msg: err.json().msg,
                                autoScaleConfig: request.autoScaleConfig
                            });
                            observer.error(res);
                            observer.complete();
                        });
                }, err => {
                    const res: ITCIAppEnvControlProviderResponse =  lodash.assign(request, {
                        appId: request.appId,
                        success: false,
                        msg: err.json().msg,
                        autoScaleConfig: request.autoScaleConfig
                    });
                    observer.error(res);
                    observer.complete();

                });
        });
    }

    cleanChanges() {
        this.initialStatuses = {};
        this.changes = {};
        this.tabdata = {};
        return this;
    }

    getUserAuthForEnv(): Observable<ITCIApplicationAuth> {
        return this.userService.getAuthForFunction("APP");
    }

    getNgUserService(): NgUserService {
        return this.userService;
    }
}
